import { createAuth0Client /*, Auth0Client */ } from "@auth0/auth0-spa-js";
import { jwtDecode as jwt_decode, type JwtPayload } from "jwt-decode";

const STORE_KEY_SAS_NO = "SAS_EUROBONUS_MEMBER_NUMBER";
const STORE_KEY_SAS_IS_SETUP = "OA_IS_SETUP";
const STORE_KEY_LOGIN_PAGE = "SAS_LOGIN_PAGE";
const STORE_KEY_SAS_EXPIRES_AT = "OA_LOGIN_EXPIRES_AT";

interface SASJwtPayload extends JwtPayload {
  euroBonusNr: string;
}

declare global {
  interface Window {
    // Config from Epi:
    SAS_Auth0_ClientId: string;
    SAS_Auth0_Domain: string;
    OA_DOMAIN: string;
    OA_SESSION_ID: string;
    OA_SAS_AFTER_LOGIN_PAGE: string;
    LOGIN_PAGE: string;
  }
}

let sasInit = false;

export const SetupSasOAuthLogin = async () => {
  if (sasInit) {
    return;
  }
  sasInit = true;

  const isLocalhost = window.location.host.includes("localhost");

  const isSasLoginPage = () => {
    return document.body.classList.contains("page-type-applycontainerpage") || document.body.classList.contains("page-type-oapage");
  };

  const getLocalStorageNaively = (key: string) => {
    let value = "";
    try {
      value = window.localStorage.getItem(key) || "";
    } catch (e) {
      console.warn(`session storage not working, tried to fetch ${key}`);
      return false;
    }
    return value;
  };
  const setLocalStorageNaively = (key: string, value: string) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      console.warn(`session storage not working, tried to set key: ${key}`);
      return false;
    }
    return value;
  };

  const clearStorage = () => {
    try {
      window.localStorage.removeItem(STORE_KEY_SAS_NO);
      window.localStorage.removeItem(STORE_KEY_SAS_IS_SETUP);
      window.localStorage.removeItem(STORE_KEY_LOGIN_PAGE);
      window.localStorage.removeItem(STORE_KEY_SAS_EXPIRES_AT);
    } catch (e) {}
  };

  const onLogoutButtonClick = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    let logoutPage = "";

    try {
      const storage = window.localStorage.getItem(STORE_KEY_LOGIN_PAGE);
      logoutPage = storage ? `${storage}?logout=true` : "";
    } catch (e) {}
    const btn = e.currentTarget;

    if (!(btn instanceof HTMLAnchorElement) && !(btn instanceof HTMLButtonElement)) {
      return;
    }

    if (btn.dataset.confirm) {
      const result = window.confirm(btn.dataset.textConfirm);
      if (!result) {
        return;
      }
      if (btn.dataset.gotoLoginpage && logoutPage) {
        window.location.href = logoutPage;
        return;
      }
    }
    clearStorage();

    auth0Client.logout({
      logoutParams: {
        returnTo: `${window.location.origin}${window.location.pathname}#onboarding`,
      },
    });
  };

  const setLoginExpiry = (hours = 24) => {
    try {
      const expiresAt = Date.now() + hours * 60 * 60 * 1000;
      window.localStorage.setItem(STORE_KEY_SAS_EXPIRES_AT, String(expiresAt));
    } catch (e) {
      console.warn("session storage not working, tried to set expiry");
      return false;
    }
    return true;
  };

  const isLoginExpired = () => {
    let raw = "";
    try {
      raw = window.localStorage.getItem(STORE_KEY_SAS_EXPIRES_AT) || "";
    } catch (e) {
      return true;
    }
    if (!raw) {
      return true;
    }
    const expiresAt = Number(raw);
    if (!Number.isFinite(expiresAt)) {
      return true;
    }
    return Date.now() > expiresAt;
  };

  document.querySelectorAll(".js-logout-link").forEach((el) =>
    el.addEventListener("click", (e) => {
      onLogoutButtonClick(e);
    })
  );

  const showLoginLogoutButtons = (isLoggedIn: boolean) => {
    if (isLoggedIn) {
      document.querySelectorAll<HTMLAnchorElement>(".js-login-link").forEach((el) => (el.style.display = "none"));
      document.querySelectorAll<HTMLAnchorElement>(".js-become-member").forEach((el) => (el.style.display = "none"));
      document.querySelectorAll<HTMLAnchorElement>(".js-sas-apply").forEach((el) => (el.style.display = ""));
      document.querySelectorAll<HTMLAnchorElement>(".js-logout-link").forEach((el) => (el.style.display = ""));
      const cta = document.querySelector<HTMLAnchorElement>(".header-cta");
      if (cta && (document.body.classList.contains("page-type-applycontainerpage") || document.body.classList.contains("page-type-oapage"))) {
        cta.style.display = "none";
      }
    }
  };
  const onLoginButtonClick = async (e?: Event) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      await auth0Client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: `${window.location.origin}${window.location.pathname}`,
        },
      });
    } catch (err) {
      console.log("Log in failed", err);
    }
  };
  showLoginLogoutButtons(!!getLocalStorageNaively(STORE_KEY_SAS_NO) && !isLoginExpired());

  document.querySelectorAll(".js-login-link").forEach((el) => {
    el.removeEventListener("click", onLoginButtonClick);
    el.addEventListener("click", onLoginButtonClick);
  });

  const setIframeUrlOnOaPage = () => {
    if (document.querySelector("body.page-type-oapage") === null) {
      return;
    }
    let sasEurobonusMemberNumber = getLocalStorageNaively(STORE_KEY_SAS_NO);
    if (!sasEurobonusMemberNumber) {
      console.warn("Error: SAS Eurobonus number missing");
      if (window.LOGIN_PAGE) {
        const warnEl = document.querySelector<HTMLDivElement>(".oa-warn-sas-login");
        if (warnEl) {
          warnEl.style.display = "block";
        }
      }
      return;
    }
    const iframe = document.querySelector<HTMLIFrameElement>(".apply-section-iframe");
    if (!iframe) {
      console.warn("Error: iFrame element missing");
      return;
    }

    iframe.src = `${window.OA_DOMAIN}${window.OA_SESSION_ID}?termid.lock-1122=${sasEurobonusMemberNumber}&url=${window.location.origin}`;
  };
  setIframeUrlOnOaPage();

  if (!isSasLoginPage()) {
    return;
  }

  const configureClient = async () => {
    if (!window.SAS_Auth0_ClientId) {
      console.warn("Missing SAS_Auth0_ClientId");
    }
    if (!window.SAS_Auth0_Domain) {
      console.warn("Missing SAS_Auth0_Domain");
    }
    const config = {
      domain: window.SAS_Auth0_Domain ? window.SAS_Auth0_Domain : "auth-stage.flysas.com",
      clientId: window.SAS_Auth0_ClientId ? window.SAS_Auth0_ClientId : "aLcvqMwA5CVuIiQCKtHDCeDM2NNZ05BR",
    };
    return createAuth0Client({
      domain: config.domain,
      clientId: config.clientId,
      authorizationParams: {
        audience: "eb-partner-api",
        redirect_uri: `${window.location.origin}${window.location.pathname}`, // "/korten/ansok/",
      },
    });
  };
  let auth0Client = await configureClient();

  if (isLoginExpired()) {
    clearStorage();
  }

  const redirectToEpiOaPage = async (target?: EventTarget | null) => {
    const OA_SAS_AFTER_LOGIN_PAGE = window.OA_SAS_AFTER_LOGIN_PAGE;

    // to test OA page locally without allowListed domain (localhost) at SAS
    if (isLocalhost && target) {
      (target as HTMLAnchorElement).href = `${OA_SAS_AFTER_LOGIN_PAGE}`;

      if (getLocalStorageNaively(STORE_KEY_SAS_NO)) {
        window.scrollTo(0, 0);
        window.location.href = `${OA_SAS_AFTER_LOGIN_PAGE}`;
      }
    }

    const accessToken = await auth0Client.getTokenSilently();
    const decodedToken: SASJwtPayload = jwt_decode(accessToken);
    setLocalStorageNaively(STORE_KEY_SAS_NO, decodedToken.euroBonusNr);

    setLocalStorageNaively(STORE_KEY_LOGIN_PAGE, `${window.location.origin}${window.location.pathname}`);

    setLoginExpiry(24);

    window.scrollTo(0, 0);
    window.location.href = `${OA_SAS_AFTER_LOGIN_PAGE}`;
  };

  const onApplyCardButtonClick = async (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    redirectToEpiOaPage(e.target);
  };
  document.querySelectorAll(".js-sas-apply").forEach((el) => el.addEventListener("click", onApplyCardButtonClick));

  const handleLogoutRedirectCallback = () => {
    if (window.location.search.includes("logout=true")) {
      clearStorage();
      window.history.replaceState({}, document.title, window.location.pathname);
      auth0Client.logout({
        logoutParams: {
          returnTo: `${window.location.origin}${window.location.pathname}`,
        },
      });
    }
  };
  handleLogoutRedirectCallback();

  const handleLoginRedirectCallback = () => {
    if (window.location.hash.includes("#login")) {
      onLoginButtonClick();
    }
  };
  handleLoginRedirectCallback();

  const handleRedirectCallback = async () => {
    if (location.search.includes("state=") && (location.search.includes("code=") || location.search.includes("error="))) {
      const result = await auth0Client.handleRedirectCallback();
      window.history.replaceState({}, document.title, window.location.pathname);
      return result;
    }
  };
  handleRedirectCallback().then(async () => {
    const isAuthenticated = await auth0Client.isAuthenticated();
    if (!isAuthenticated) {
      if (getLocalStorageNaively(STORE_KEY_SAS_NO) === null) {
        console.log("> User not authenticated");
      }
      return;
    }
    console.log("> User is authenticated");

    redirectToEpiOaPage();
  });
};
SetupSasOAuthLogin();
